import React, { useCallback, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import TableContainer from '@material-ui/core/TableContainer';
import { useSnackbar } from 'notistack';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import { Button, createStyles, makeStyles, Theme } from '@material-ui/core';
import { Close, GetApp } from '@material-ui/icons';
import TablePagination from 'components/atoms/TablePagination/TablePagination';
import Spin from 'components/atoms/Spin/Spin';
import { Order } from 'utils/sort';
import TableSortCell from 'components/atoms/TablePagination/TableSortCell';
import {
    closeDeliveryList,
    downloadFlightOrderReport,
    downloadReturnCertificate,
    FlightDto,
} from '../../../api/flight';
import { CustomerDto, getPagedByDelivery, OrderDto } from '../../../api/orders';

interface Props {
    id: number | string;
    item: FlightDto;
    hasAccessToCloseList: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        downloadButton: {
            position: 'absolute',
            right: 0,
            top: -theme.spacing(7),
        },
        button: {
            marginLeft: theme.spacing(2),
        },
    })
);

let iframe: HTMLIFrameElement;

const printPdf = function (url: string) {
    if (!iframe) {
        iframe = document.createElement('iframe');
        document.body.appendChild(iframe);

        iframe.style.display = 'none';
        iframe.onload = function () {
            setTimeout(() => {
                iframe.focus();
                iframe.contentWindow?.print();
            }, 1);
        };
    }

    iframe.src = url;
};

const FlightDetails = ({ id, item, hasAccessToCloseList }: Props) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    // Table and stuff
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<OrderDto[]>([]);

    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [total, setTotal] = useState(0);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof FlightDto>('id');

    // Fetch
    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const x = await getPagedByDelivery(id, page, perPage);
            setItems(x.content);
            setTotal(x.total_elements);
        } catch {
            enqueueSnackbar('Ошибка при получении данных', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [id, page, perPage, enqueueSnackbar]);

    const handleRequestSort = (property: keyof FlightDto) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleDownload = useCallback(
        async (type?: string) => {
            try {
                setLoading(true);
                const response = await (type
                    ? downloadReturnCertificate(id)
                    : downloadFlightOrderReport(id));
                const contentType = response.headers['content-type'];
                const blob = new Blob([response.data], { type: contentType, endings: 'native' });
                const url = window.URL.createObjectURL(blob);
                printPdf(url);
            } catch (e) {
                enqueueSnackbar(`Ошибка при выгрузке файла: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            } finally {
                setLoading(false);
            }
        },
        [enqueueSnackbar, id]
    );
    const handleCloseDeliveryList = useCallback(async () => {
        try {
            setLoading(true);
            const response = await closeDeliveryList(item.code);
            if (response?.result === 'error') {
                enqueueSnackbar(`Ошибка: ${response?.message}`, {
                    variant: 'error',
                });
            } else if (response?.result === 'success') {
                enqueueSnackbar(`${response?.message || 'Доставочный лист закрыт!'}`, {
                    variant: 'success',
                });
            }
        } catch (e) {
            enqueueSnackbar(`Ошибка при выгрузке файла: ${e.response?.data?.message || e}`, {
                variant: 'success',
            });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, item]);

    useEffect(() => {
        fetch();
    }, [fetch]);
    return (
        <Box position={'relative'}>
            <Box className={classes.downloadButton} display="flex">
                {hasAccessToCloseList && item.delivery_list_status.code === 'SEND' && (
                    <Button
                        className={classes.button}
                        variant="contained"
                        color="secondary"
                        onClick={handleCloseDeliveryList}
                        startIcon={<Close />}
                        disabled={loading}
                    >
                        Закрыть ДЛ
                    </Button>
                )}
                <Button
                    className={classes.button}
                    variant="contained"
                    color="primary"
                    onClick={() => handleDownload()}
                    startIcon={<GetApp />}
                    disabled={loading}
                >
                    Доставочный лист
                </Button>
                <Button
                    className={classes.button}
                    variant="contained"
                    color="primary"
                    onClick={() => handleDownload('reference')}
                    startIcon={<GetApp />}
                    disabled={loading}
                >
                    Cправка о возврате
                </Button>
            </Box>

            <Box position={'relative'}>
                {loading && <Spin />}
                <TableContainer>
                    <Table size={'medium'} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableSortCell
                                    label={'№'}
                                    orderBy={orderBy}
                                    order={order}
                                    property={'id'}
                                    onRequestSort={handleRequestSort}
                                />
                                <TableCell>Номер заявки</TableCell>
                                <TableCell>Особые отметки</TableCell>
                                <TableCell>Статус</TableCell>
                                <TableCell>Регион</TableCell>
                                <TableCell>Третья доставка</TableCell>
                                <TableCell>Отправитель</TableCell>
                                <TableCell>Получатель</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={index} hover={true}>
                                    <TableCell>{page * perPage + index + 1}</TableCell>
                                    <TableCell>{item.order_number}</TableCell>
                                    <TableCell>{item.order_detail?.special_marks}</TableCell>
                                    <TableCell>{item.order_status?.description}</TableCell>
                                    <TableCell>{item.region?.description}</TableCell>
                                    <TableCell>
                                        {item.order_detail?.is_third_delivery !== undefined &&
                                            (item.order_detail?.is_third_delivery ? 'Да' : 'Нет')}
                                    </TableCell>
                                    <TableCell>
                                        {item.customers
                                            .filter(
                                                (c: CustomerDto) => c.relation_type !== 'RECEIVER'
                                            )
                                            .map((client: CustomerDto, clientIdx: number) => (
                                                <span key={clientIdx}>
                                                    {`${client.customer?.name} ${
                                                        client.customer?.surname || ''
                                                    }`}
                                                    , {client.customer?.phone_number}
                                                </span>
                                            ))}
                                    </TableCell>
                                    <TableCell>
                                        {item.customers
                                            .filter(
                                                (c: CustomerDto) => c.relation_type === 'RECEIVER'
                                            )
                                            .map((client: CustomerDto, clientIdx: number) => (
                                                <span key={clientIdx}>
                                                    {`${client.customer?.name} ${
                                                        client.customer?.surname || ''
                                                    }`}
                                                    , {client.customer?.phone_number}
                                                </span>
                                            ))}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <TablePagination
                total={total}
                perPage={perPage}
                page={page}
                onChangePage={setPage}
                onChangeRowsPerPage={setPerPage}
            />
        </Box>
    );
};

export default FlightDetails;
